// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.
//
// D-CRX-71 — the non-lab screening panel inside the Clinical Review record.
// Renders exactly what the determination gate evaluates (one engine, server-side), so a pharmacist
// never sees a green panel and then gets refused at Approve.
//
// Layout: a rule library is twenty-plus questions, and as one long column it read as a wall the
// pharmacist scrolled past. The questions are independent of one another, so they tile — a
// responsive grid of cards, ordered by how hard each one stops the approval, with two filter rows
// — state and clinical domain — so the pharmacist reads the work rather than the library.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { ShieldAlert, Check, AlertTriangle, HelpCircle, MinusCircle } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'

const card: React.CSSProperties = { border: '1px solid #dde5ec', borderRadius: 12, padding: 15, marginTop: 14 }
const inp: React.CSSProperties = { fontSize: 12, padding: '4px 8px', border: '1px solid #dde5ec', borderRadius: 7, background: '#fff' }

// One card = one rule. The visual weight is the SEVERITY, not the answer: an unmet HARD_HOLD and
// an unanswered HARD_HOLD both stop the therapy, so they read the same.
const STATE_UI: Record<string, { icon: typeof Check; color: string; bg: string; label: string }> = {
  MET: { icon: Check, color: '#0d6b52', bg: '#e1f4ec', label: 'Met' },
  UNMET: { icon: AlertTriangle, color: '#b23a48', bg: '#fbecee', label: 'Unmet' },
  UNANSWERED: { icon: HelpCircle, color: '#93610f', bg: '#fff7e6', label: 'Not answered' },
  NOTEVALUABLE: { icon: MinusCircle, color: '#6b7280', bg: '#eef1f4', label: 'Cannot evaluate' },
  OVERRIDDEN: { icon: Check, color: '#185FA5', bg: '#eaf2fb', label: 'Attested past' },
  NOTAPPLICABLE: { icon: MinusCircle, color: '#8496a5', bg: 'transparent', label: 'N/A' },
}
const SEV_TAG: Record<string, React.CSSProperties> = {
  HARD_HOLD: { background: '#fbecee', color: '#b23a48' },
  SOFT_HOLD: { background: '#fff7e6', color: '#93610f' },
  ADVISORY: { background: '#eef1f4', color: '#475569' },
}
// The left edge is the only thing that survives peripheral vision, so it carries the one fact that
// matters at a glance: how hard this rule stops the approval.
const EDGE: Record<string, string> = { HARD_HOLD: '#b23a48', SOFT_HOLD: '#d9a441', ADVISORY: '#c7d2dc' }

const domainLabel = (d: string) => d.replace(/_/g, ' ').toLowerCase().replace(/^./, c => c.toUpperCase())

type Filter = 'attention' | 'settled' | 'all'

// readOnly (ASP 2026-10-11 #15): a decided review — the answers and attestations are shown, not changed;
// the view tabs, domain chips and "Why" stay usable so the full review can be read.
export default function CareRxScreeningPanel({ therapyId, readOnly = false }: { therapyId: string; readOnly?: boolean }) {
  const [data, setData] = useState<api.ScreeningEvaluation | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [overrideFor, setOverrideFor] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [why, setWhy] = useState<Record<string, boolean>>({})
  const [filter, setFilter] = useState<Filter | null>(null)
  const [domain, setDomain] = useState<string>('ALL')

  // P0 B9 (UX audit 2026-10-04): a failed evaluation used to set data to null, and null rendered
  // nothing — on a gated approval, "the panel is gone" reads as "nothing to screen". A failure now
  // says screening could not run, and a failed RE-load keeps the last answers on screen.
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const load = useCallback(() => {
    api.getScreeningEvaluation(therapyId)
      .then(d => { setData(d); setLoadErr(null) })
      .catch(e => setLoadErr(friendlyError(e)))
  }, [therapyId])
  useEffect(() => { load() }, [load])

  const findings = useMemo(() => data?.findings ?? [], [data])
  const blocking = useMemo(() => findings.filter(f => f.blocks), [findings])
  // Default to the short list when there is one — a pharmacist opening a record wants the work,
  // not the library. With nothing blocking, the whole set shows.
  const active: Filter = filter ?? (blocking.length ? 'attention' : 'all')

  // Domains hold one or two rules each, so domain headings cost more vertical space than they save
  // — the domain rides on the card instead and everything tiles in a single grid. What decides
  // reading order is severity, not subject: hard holds first, advisories last.
  const shown = useMemo(() => {
    const keep = findings.filter(f =>
      (domain === 'ALL' || f.domain === domain)
      && (active === 'all' ? true : active === 'attention' ? f.blocks : !f.blocks))
    const sevRank = (s: string) => (s === 'HARD_HOLD' ? 0 : s === 'SOFT_HOLD' ? 1 : 2)
    return keep.slice().sort((a, b) =>
      Number(b.blocks) - Number(a.blocks)
      || sevRank(a.severity) - sevRank(b.severity)
      || (a.domain || '').localeCompare(b.domain || '')
      || a.rule_ref.localeCompare(b.rule_ref))
  }, [findings, active, domain])

  const domains = useMemo(() => {
    const c = new Map<string, number>()
    for (const f of findings) if (f.domain) c.set(f.domain, (c.get(f.domain) ?? 0) + 1)
    return [...c.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [findings])

  // A therapy whose drug has no published rules has nothing to ask. Say that in one line rather
  // than rendering nothing: an empty space reads as "the panel is broken", and a pharmacist needs
  // to know the difference between "screened, all clear" and "there is nothing to screen against".
  if (!data && loadErr) return (
    <section role="alert" style={{ ...card, padding: '10px 15px', borderColor: '#f0a8a8', background: '#fef2f2' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#7f1d1d', flexWrap: 'wrap' }}>
        <AlertTriangle size={14} color="#b91c1c" />
        <b>Screening could not run.</b> The questions for this therapy were not loaded, so nothing here is screened. {loadErr}
        <button type="button" onClick={load} style={{ ...inp, cursor: 'pointer', fontWeight: 600 }}>Retry</button>
      </div>
    </section>
  )
  if (!data) return (
    <section style={{ ...card, padding: '10px 15px', fontSize: 11.5, color: '#8496a5' }}>Loading screening…</section>
  )
  if (data.rule_count === 0) {
    // Two very different silences. "Not linked" means screening could not run — and with the gate
    // enforced that is a therapy passing a check nobody performed, so it is stated in warning
    // colour with the fix named, not filed under "nothing to see".
    const unlinked = data.drug_linked === false
    return (
      <section style={{ ...card, padding: '10px 15px', borderColor: unlinked ? '#f0c000' : '#dde5ec',
                        background: unlinked ? '#fffbeb' : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, flexWrap: 'wrap',
                      color: unlinked ? '#92610f' : '#8496a5' }}>
          <ShieldAlert size={13} color={unlinked ? '#d97706' : '#b6c2cd'} />
          <span style={{ fontWeight: 600, color: unlinked ? '#92610f' : '#5b6b7c' }}>Screening</span>
          {unlinked
            ? <>this drug is not linked to Drug Reference, so no screening rule can apply to it
                {data.gate_enforced && <b> — the gate is enforced and this therapy passes it unscreened.</b>}
                {' '}Link the drug in Drug Reference to screen it.</>
            : <>no non-lab rules are published for this drug{data.drug_category ? ` or for ${data.drug_category}` : ''} (D-CRX-71)</>}
        </div>
      </section>
    )
  }

  const answer = async (ruleId: string, value: 'YES' | 'NO' | 'UNKNOWN' | 'NA', override?: string) => {
    setBusy(ruleId); setMsg(null)
    try {
      await api.answerScreeningRule(therapyId, { rule_id: ruleId, answer: value, override: !!override, override_reason: override })
      setOverrideFor(null); setReason(''); load()
    } catch (e) { setMsg(friendlyError(e)) } finally { setBusy(null) }
  }

  const counts = { attention: blocking.length, settled: findings.length - blocking.length, all: findings.length }
  const tab = (k: Filter): React.CSSProperties => ({
    ...inp, cursor: 'pointer', fontWeight: active === k ? 700 : 500,
    background: active === k ? '#eef4fb' : '#fff', color: active === k ? '#185FA5' : '#41566a',
    borderColor: active === k ? '#b9d3ef' : '#dde5ec',
  })
  const chip = (on: boolean): React.CSSProperties => ({
    ...inp, padding: '2px 9px', fontSize: 10.5, cursor: 'pointer', borderRadius: 100,
    fontWeight: on ? 700 : 500, background: on ? '#eef4fb' : '#fff',
    color: on ? '#185FA5' : '#5b6b7c', borderColor: on ? '#b9d3ef' : '#e6ecf2',
  })

  return (
    <section style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
        <ShieldAlert size={15} color="#93610f" />
        <span style={{ fontSize: 13, fontWeight: 660 }}>Screening</span>
        <span style={{ fontSize: 10.5, color: '#8496a5' }}>(D-CRX-71 · non-lab · {data.rule_count} rules)</span>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: blocking.length ? '#b23a48' : '#0d6b52', fontWeight: 600 }}>
          {blocking.length ? `${blocking.length} unresolved` : 'all clear'}
        </span>
      </div>
      {/* Shadow mode is stated, not implied: a pharmacist must know whether this panel can stop an
          approval or is only telling them what would stop it once the gate is on. */}
      <div style={{ fontSize: 11.5, color: data.gate_enforced ? '#b23a48' : '#41566a', marginBottom: 10 }}>
        {data.gate_enforced
          ? 'Enforced — an unresolved HARD_HOLD refuses approval; a SOFT_HOLD needs an attested reason.'
          : 'Not enforced yet — findings are shown for review and do not block approval.'}
        {data.is_initiation ? ' · initiation' : ' · continuation'}
      </div>
      {msg && <div style={{ fontSize: 12, color: '#b23a48', background: '#fbecee', borderRadius: 8, padding: '7px 11px', marginBottom: 8 }}>{msg}</div>}
      {loadErr && <div role="alert" style={{ fontSize: 12, color: '#b23a48', background: '#fbecee', borderRadius: 8, padding: '7px 11px', marginBottom: 8 }}>
        Could not refresh screening — the answers below may be out of date. {loadErr} <button type="button" onClick={load} style={{ ...inp, cursor: 'pointer' }}>Retry</button></div>}

      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        <button onClick={() => setFilter('attention')} style={tab('attention')}>Needs attention · {counts.attention}</button>
        <button onClick={() => setFilter('settled')} style={tab('settled')}>Settled · {counts.settled}</button>
        <button onClick={() => setFilter('all')} style={tab('all')}>All · {counts.all}</button>
      </div>

      {domains.length > 1 && (
        <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 10.5, color: '#8496a5' }}>Domain</span>
          <button onClick={() => setDomain('ALL')} style={chip(domain === 'ALL')}>All</button>
          {domains.map(([d, n]) => (
            <button key={d} onClick={() => setDomain(d)} style={chip(domain === d)}>{domainLabel(d)} · {n}</button>
          ))}
        </div>
      )}

      {shown.length === 0 && (
        <div style={{ fontSize: 12, color: '#0d6b52', background: '#e1f4ec', borderRadius: 8, padding: '9px 12px' }}>
          {active === 'attention' ? 'Nothing is holding this therapy up.' : 'Nothing to show here.'}
        </div>
      )}

      {/* auto-fill, not auto-fit: a lone card keeps a readable column width instead of stretching a
          one-line question across the whole record. */}
      <div style={{ display: 'grid', gap: 9, alignItems: 'start',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
        {shown.map(f => {
          const ui = STATE_UI[f.state] ?? STATE_UI.NOTEVALUABLE
          const Icon = ui.icon
          const showWhy = !!why[f.rule_id]
          const hasWhy = !!(f.detail || (f.state === 'UNMET' && f.remediation_text) || f.citation)
          return (
          <div key={f.rule_id} style={{
            border: '1px solid #e6ecf2', borderLeft: `3px solid ${EDGE[f.severity] ?? '#c7d2dc'}`,
            borderRadius: 10, padding: '9px 11px', background: f.blocks ? '#fffdfa' : '#fff',
            display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: ui.bg, color: ui.color, borderRadius: 100, padding: '1px 8px', fontSize: 10.5, fontWeight: 600 }}>
                <Icon size={12} /> {ui.label}
              </span>
              <span style={{ ...SEV_TAG[f.severity], fontSize: 10, fontWeight: 700, borderRadius: 100, padding: '1px 8px' }}>{f.severity.replace('_', ' ')}</span>
              {/* Nothing above the card names the domain, so the card carries it. */}
              {f.domain && (
                <span style={{ fontSize: 10, fontWeight: 600, color: '#5b6b7c', background: '#f2f6fa', borderRadius: 100, padding: '1px 8px' }}>{domainLabel(f.domain)}</span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 10, color: '#a3b1bd', fontFamily: 'ui-monospace' }}>{f.rule_ref}</span>
            </div>

            <div style={{ fontSize: 12.5, lineHeight: 1.35 }}>{f.prompt_text}</div>

            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
              {(['YES', 'NO', 'UNKNOWN', 'NA'] as const).map(v => (
                <button key={v} disabled={readOnly || busy === f.rule_id} onClick={() => answer(f.rule_id, v)}
                  style={{ ...inp, padding: '3px 9px', fontSize: 11, cursor: 'pointer', fontWeight: f.answer === v ? 700 : 500,
                           background: f.answer === v ? '#185FA5' : '#fff', color: f.answer === v ? '#fff' : '#33404f' }}>{v}</button>
              ))}
              {hasWhy && (
                <button onClick={() => setWhy(w => ({ ...w, [f.rule_id]: !showWhy }))}
                  style={{ background: 'none', border: 'none', padding: 0, marginLeft: 'auto', fontSize: 11, color: '#185FA5', cursor: 'pointer', fontWeight: 600 }}>
                  {showWhy ? 'Hide' : 'Why'}
                </button>
              )}
            </div>

            {/* HARD_HOLD is deliberately absent here: it cannot be attested past, only resolved. */}
            {!readOnly && f.severity === 'SOFT_HOLD' && f.state === 'UNMET' && (
              overrideFor === f.rule_id ? (
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Attestation reason (required)" style={{ ...inp, flex: '1 1 150px', minWidth: 0 }} />
                  <button disabled={!reason.trim() || busy === f.rule_id} onClick={() => answer(f.rule_id, (f.answer as 'YES') || 'YES', reason.trim())}
                    style={{ ...inp, cursor: reason.trim() ? 'pointer' : 'not-allowed', background: '#93610f', color: '#fff', border: 'none', fontWeight: 600 }}>Attest</button>
                  <button onClick={() => { setOverrideFor(null); setReason('') }} style={{ ...inp, cursor: 'pointer' }}>✕</button>
                </div>
              ) : (
                <button onClick={() => { setOverrideFor(f.rule_id); setReason('') }}
                  style={{ ...inp, alignSelf: 'flex-start', padding: '3px 9px', fontSize: 11, cursor: 'pointer', color: '#93610f' }}>Attest past…</button>
              )
            )}

            {showWhy && (
              <div style={{ borderTop: '1px solid #f0f4f7', paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {f.detail && <div style={{ fontSize: 11, color: '#8496a5' }}>{f.detail}</div>}
                {f.state === 'UNMET' && f.remediation_text && (
                  <div style={{ fontSize: 11.5, color: '#93610f' }}>{f.remediation_text}</div>
                )}
                {/* The citation is the point: a rule the pharmacist cannot trace to a label
                    section is a rule they cannot defend. */}
                {f.citation && <div style={{ fontSize: 10.5, color: '#8496a5' }}>{f.citation}</div>}
              </div>
            )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
